// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { useClientData } from '@shared/advisory/client-profile/providers';
import { useConst } from '@shared/ui-kit/hooks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const useAdvisoryConditions = () => {
  const { t } = useTranslation();
  const { client, statusMap } = useClientData();

  const isActiveClient = useMemo(() => client.status === 'active', [client.status]);

  const advisoryTypeMap = useConst(
    () =>
      new Map([
        [
          'discretionary',
          {
            id: 'discretionary',
            name: t('activation-step2.advisory-types.discretionary.label', {
              ns: 'components',
            }),
            description: t('activation-step2.advisory-types.discretionary.name', {
              ns: 'components',
            }),
          },
        ],
        [
          'non-independent',
          {
            id: 'non-independent',
            name: t('activation-step2.advisory-types.non-independent.label', {
              ns: 'components',
            }),
            description: t('activation-step2.advisory-types.non-independent.name', {
              ns: 'components',
            }),
          },
        ],
        [
          'independent',
          {
            id: 'independent',
            name: t('activation-step2.advisory-types.independent.label', {
              ns: 'components',
            }),
            description: t('activation-step2.advisory-types.independent.name', {
              ns: 'components',
            }),
          },
        ],
        [
          'inactive',
          {
            id: 'inactive',
            name: t('activation-step2.advisory-types.inactive.label', {
              ns: 'components',
            }) as string,
            description: t('activation-step2.advisory-types.inactive.name', {
              ns: 'components',
            }) as string,
          },
        ],
      ]),
  );

  /** Institutions included in the advisory proposal (with products.accounts). */
  const advisedInstitutions = useMemo(
    () => client.advisory_proposal?.institutions ?? [],
    [client.advisory_proposal?.institutions],
  );

  const advisoryData = useMemo(
    () => ({
      type: advisoryTypeMap.get(
        client.advisory_proposal?.advisory_type ?? 'inactive',
      )!,
      commission: client.advisory_proposal?.commission_managed_assets ?? 0,
      /** Institutions in the proposal (with products.accounts). */
      advisedInstitutions,
    }),
    [
      advisoryTypeMap,
      client.advisory_proposal?.advisory_type,
      client.advisory_proposal?.commission_managed_assets,
      advisedInstitutions,
    ],
  );

  return {
    isActiveClient,
    advisoryData,
    statusMap,
  } as const;
};
